import { useEffect, useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { AutomationsTab } from './business-settings/AutomationsTab';
import { PlanAndBillingTab } from './business-settings/PlanAndBillingTab';  // iter334cg
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import api from '../../services/api';
import { toast } from 'sonner';
import { 
  Clock, 
  Save, 
  Sun, 
  Phone, 
  Mail, 
  MessageCircle, 
  Building2, 
  Globe,
  MapPin,
  Plus,
  Edit2,
  Trash2,
  Check,
  Calendar,
  Loader2,
  AlertTriangle,
  ShieldOff,
  Upload,
  Image as ImageIcon,
  X,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Zap,
  Palette,
  CreditCard  // iter334cg — Plan & Billing tab icon
} from 'lucide-react';
import { Textarea } from '../../components/ui/textarea';
import ThemePicker from './ThemePicker';

const DAYS = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' }
];

const DEFAULT_HOURS = DAYS.map(d => ({
  day: d.key,
  is_open: true,
  open_time: d.key === 'saturday' || d.key === 'sunday' ? '09:00' : '16:00',
  close_time: d.key === 'saturday' || d.key === 'sunday' ? '18:00' : '21:00'
}));

const BusinessHours = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(() => new URLSearchParams(window.location.search).get('tab') || 'seasons');
  
  // Seasons state
  const [seasons, setSeasons] = useState([]);
  const [_selectedSeason, setSelectedSeason] = useState(null);
  const [showSeasonDialog, setShowSeasonDialog] = useState(false);
  const [editingSeason, setEditingSeason] = useState(null);
  const [seasonForm, setSeasonForm] = useState({
    name: '',
    start_date: '',
    end_date: '',
    hours: DEFAULT_HOURS
  });
  
  // Delete season confirmation
  const [deleteSeasonDialog, setDeleteSeasonDialog] = useState(false);
  const [seasonToDelete, setSeasonToDelete] = useState(null);
  
  // Business settings state
  const [settings, setSettings] = useState({
    business_name: 'Cricket Academy',
    business_tagline: '',
    business_phone: '',
    whatsapp_number: '',
    delivery_email: '',
    address: '',
    website: '',
    // iter323 — Optional external link rendered in the dashboard
    // sidebar. Both label + URL must be filled for the link to show.
    external_link_label: '',
    external_link_url: '',
    social_links: {
      facebook: '',
      instagram: '',
      twitter: ''
    },
    maintenance_mode: false,
    maintenance_message: 'We are currently performing scheduled maintenance. Please check back shortly.',
    logo_id: null,
    logo_size: 'medium',
    logo_alignment: 'left',
    background_image_url: null,
    background_opacity: 0.1
  });
  
  // Logo upload state
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [deletingLogo, setDeletingLogo] = useState(false);
  const [savingLogoSize, setSavingLogoSize] = useState(false);
  const [savingLogoAlignment, setSavingLogoAlignment] = useState(false);
  
  // Background image state
  const [uploadingBackground, setUploadingBackground] = useState(false);
  const [deletingBackground, setDeletingBackground] = useState(false);
  const [savingOpacity, setSavingOpacity] = useState(false);
  
  // Logo size options
  const logoSizeOptions = [
    { value: 'small', label: 'Small', height: '40px' },
    { value: 'medium', label: 'Medium', height: '56px' },
    { value: 'large', label: 'Large', height: '80px' },
    { value: 'xlarge', label: 'Extra Large', height: '96px' }
  ];
  
  // Logo alignment options
  const logoAlignmentOptions = [
    { value: 'left', label: 'Left', icon: 'AlignLeft' },
    { value: 'center', label: 'Center', icon: 'AlignCenter' },
    { value: 'right', label: 'Right', icon: 'AlignRight' }
  ];
  
  // Timezone settings state
  const [timezoneSettings, setTimezoneSettings] = useState({
    timezone: 'America/New_York',
    timezone_label: 'Eastern Time (ET) - New York',
    available_timezones: []
  });
  const [currentServerTime, setCurrentServerTime] = useState(null);
  const [savingTimezone, setSavingTimezone] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [seasonsRes, settingsRes, timezoneRes] = await Promise.all([
        api.get('/admin/seasons'),
        api.get('/admin/business-settings'),
        api.get('/admin/timezone-settings')
      ]);
      setSeasons(seasonsRes.data.seasons || []);
      setSettings(settingsRes.data);
      setTimezoneSettings(timezoneRes.data);
      
      // Select active season by default
      const active = seasonsRes.data.seasons?.find(s => s.is_active);
      if (active) {
        setSelectedSeason(active);
      }
      
      // Fetch current server time
      fetchCurrentTime();
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchCurrentTime = async () => {
    try {
      const response = await api.get('/admin/current-time');
      setCurrentServerTime(response.data);
    } catch (error) {
      console.error('Error fetching current time:', error);
    }
  };
  
  const handleSaveTimezone = async () => {
    setSavingTimezone(true);
    try {
      const response = await api.put('/admin/timezone-settings', {
        timezone: timezoneSettings.timezone
      });
      setTimezoneSettings(prev => ({
        ...prev,
        timezone: response.data.timezone,
        timezone_label: response.data.timezone_label
      }));
      toast.success('Timezone settings updated');
      // Refresh current time
      fetchCurrentTime();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update timezone');
    } finally {
      setSavingTimezone(false);
    }
  };

  const updateSettings = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const updateSocialLink = (platform, value) => {
    setSettings(prev => ({
      ...prev,
      social_links: {
        ...prev.social_links,
        [platform]: value
      }
    }));
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/admin/business-settings', settings);
      toast.success('Business settings updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  // Logo upload/delete handlers
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo file must be less than 5MB');
      return;
    }
    
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/admin/logo/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Update local state with new logo_id
      setSettings(prev => ({
        ...prev,
        logo_id: response.data.logo_id
      }));
      
      // Update localStorage cache so other pages show new logo immediately
      const newLogoUrl = `/api/admin/logo/${response.data.logo_id}`;
      localStorage.setItem('businessLogoUrl', newLogoUrl);
      
      toast.success('Logo uploaded successfully');
    } catch (error) {
      console.error('Logo upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
      // Reset file input
      e.target.value = '';
    }
  };
  
  const handleLogoDelete = async () => {
    setDeletingLogo(true);
    try {
      await api.delete('/admin/logo');
      
      // Update local state
      setSettings(prev => ({
        ...prev,
        logo_id: null
      }));
      
      // Clear localStorage cache so other pages show default logo
      localStorage.removeItem('businessLogoUrl');
      
      toast.success('Logo removed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove logo');
    } finally {
      setDeletingLogo(false);
    }
  };
  
  const handleLogoSizeChange = async (newSize) => {
    // Update local state immediately for responsiveness
    setSettings(prev => ({
      ...prev,
      logo_size: newSize
    }));
    
    // Update localStorage for immediate effect on other pages
    localStorage.setItem('businessLogoSize', newSize);
    
    // Save to backend
    setSavingLogoSize(true);
    try {
      await api.put('/admin/business-settings', { logo_size: newSize });
      toast.success('Logo size updated');
    } catch (error) {
      toast.error('Failed to save logo size');
      // Revert on error
      setSettings(prev => ({
        ...prev,
        logo_size: settings.logo_size
      }));
    } finally {
      setSavingLogoSize(false);
    }
  };
  
  const handleLogoAlignmentChange = async (newAlignment) => {
    const oldAlignment = settings.logo_alignment;
    
    // Update local state immediately for responsiveness
    setSettings(prev => ({
      ...prev,
      logo_alignment: newAlignment
    }));
    
    // Update localStorage for immediate effect on other pages
    localStorage.setItem('businessLogoAlignment', newAlignment);
    
    // Save to backend
    setSavingLogoAlignment(true);
    try {
      await api.put('/admin/business-settings', { logo_alignment: newAlignment });
      toast.success('Logo alignment updated');
    } catch (error) {
      toast.error('Failed to save logo alignment');
      // Revert on error
      setSettings(prev => ({
        ...prev,
        logo_alignment: oldAlignment
      }));
    } finally {
      setSavingLogoAlignment(false);
    }
  };

  // Background image handlers
  const handleBackgroundUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Background image must be less than 10MB');
      return;
    }
    
    setUploadingBackground(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/admin/background/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Update local state
      setSettings(prev => ({
        ...prev,
        background_image_url: response.data.url
      }));
      
      // Update localStorage for immediate effect on other pages
      localStorage.setItem('backgroundImageUrl', response.data.url);
      
      toast.success('Background image uploaded successfully');
    } catch (error) {
      console.error('Background upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload background');
    } finally {
      setUploadingBackground(false);
      e.target.value = '';
    }
  };
  
  const handleBackgroundDelete = async () => {
    setDeletingBackground(true);
    try {
      await api.delete('/admin/background');
      
      setSettings(prev => ({
        ...prev,
        background_image_url: null
      }));
      
      localStorage.removeItem('backgroundImageUrl');
      
      toast.success('Background image removed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove background');
    } finally {
      setDeletingBackground(false);
    }
  };
  
  const handleOpacityChange = async (newOpacity) => {
    const opacity = parseFloat(newOpacity);
    
    setSettings(prev => ({
      ...prev,
      background_opacity: opacity
    }));
    
    localStorage.setItem('backgroundOpacity', opacity.toString());
    
    setSavingOpacity(true);
    try {
      await api.put('/admin/business-settings', { background_opacity: opacity });
      toast.success('Background opacity updated');
    } catch (error) {
      toast.error('Failed to save opacity');
    } finally {
      setSavingOpacity(false);
    }
  };

  // Season management
  const openCreateSeasonDialog = () => {
    setEditingSeason(null);
    setSeasonForm({
      name: '',
      start_date: '',
      end_date: '',
      hours: DEFAULT_HOURS
    });
    setShowSeasonDialog(true);
  };

  const openEditSeasonDialog = (season) => {
    setEditingSeason(season);
    setSeasonForm({
      name: season.name,
      start_date: season.start_date || '',
      end_date: season.end_date || '',
      hours: season.hours || DEFAULT_HOURS
    });
    setShowSeasonDialog(true);
  };

  const updateSeasonHours = (day, field, value) => {
    setSeasonForm(prev => ({
      ...prev,
      hours: prev.hours.map(h => 
        h.day === day ? { ...h, [field]: value } : h
      )
    }));
  };

  const handleSaveSeason = async () => {
    if (!seasonForm.name.trim()) {
      toast.error('Season name is required');
      return;
    }

    setSaving(true);
    try {
      if (editingSeason) {
        await api.put(`/admin/seasons/${editingSeason.id}`, seasonForm);
        toast.success('Season updated');
      } else {
        await api.post('/admin/seasons', seasonForm);
        toast.success('Season created');
      }
      setShowSeasonDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save season');
    } finally {
      setSaving(false);
    }
  };

  const handleActivateSeason = async (seasonId) => {
    try {
      await api.put(`/admin/seasons/${seasonId}/activate`);
      toast.success('Season activated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate season');
    }
  };

  const openDeleteSeasonDialog = (seasonId) => {
    setSeasonToDelete(seasonId);
    setDeleteSeasonDialog(true);
  };

  const handleDeleteSeason = async () => {
    if (!seasonToDelete) return;
    
    try {
      await api.delete(`/admin/seasons/${seasonToDelete}`);
      toast.success('Season deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete season');
    } finally {
      setDeleteSeasonDialog(false);
      setSeasonToDelete(null);
    }
  };

  const formatTime = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not set';
    // Parse date string as local date to avoid timezone issues
    // "2026-04-30" should display as April 30, not April 29
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <DashboardLayout title="Business Settings">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-12 w-12 animate-spin text-blue-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Business Settings">
      <div className="space-y-6" data-testid="business-settings-page">
        <p className="text-slate-600">Manage your academy's business information and operating hours</p>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="flex w-full overflow-x-auto lg:w-auto lg:inline-flex mb-4">
            <TabsTrigger value="seasons" className="flex items-center gap-2" data-testid="tab-seasons">
              <Sun className="h-4 w-4" />
              Seasons & Hours
            </TabsTrigger>
            <TabsTrigger value="timezone" className="flex items-center gap-2" data-testid="tab-timezone">
              <Globe className="h-4 w-4" />
              Timezone
            </TabsTrigger>
            <TabsTrigger value="contact" className="flex items-center gap-2" data-testid="tab-contact">
              <Building2 className="h-4 w-4" />
              Contact Info
            </TabsTrigger>
            <TabsTrigger value="branding" className="flex items-center gap-2" data-testid="tab-branding">
              <Palette className="h-4 w-4" />
              Branding
            </TabsTrigger>
            <TabsTrigger value="maintenance" className="flex items-center gap-2" data-testid="tab-maintenance">
              <ShieldOff className={`h-4 w-4 ${settings.maintenance_mode ? 'text-red-500' : ''}`} />
              Maintenance
              {settings.maintenance_mode && (
                <span className="ml-1 w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
              )}
            </TabsTrigger>
            <TabsTrigger value="automations" className="flex items-center gap-2" data-testid="tab-automations">
              <Zap className="h-4 w-4" />
              Automations
            </TabsTrigger>
            {/* iter334cg — Tenant admin's read-only view of their SaaS
                plan + billing history. */}
            <TabsTrigger value="plan-billing" className="flex items-center gap-2" data-testid="tab-plan-billing">
              <CreditCard className="h-4 w-4" />
              Plan &amp; Billing
            </TabsTrigger>
          </TabsList>

          {/* Seasons Tab */}
          <TabsContent value="seasons">
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <h3 className="text-lg font-medium">Season Management</h3>
                  <p className="text-sm text-slate-500">Create and manage different operating schedules for each season</p>
                </div>
                <Button
                  onClick={openCreateSeasonDialog}
                  className="bg-blue-500 hover:bg-blue-600"
                  data-testid="create-season-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Season
                </Button>
              </div>

              {/* Seasons List */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {seasons.length === 0 ? (
                  <Card className="lg:col-span-2 border-dashed">
                    <CardContent className="py-12 text-center">
                      <Sun className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                      <p className="text-slate-500 mb-4">No seasons configured yet</p>
                      <Button onClick={openCreateSeasonDialog} variant="outline">
                        <Plus className="h-4 w-4 mr-2" />
                        Create Your First Season
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  seasons.map(season => (
                    <Card 
                      key={season.id} 
                      className={`border ${season.is_active ? 'border-green-300 bg-green-50/50' : 'border-slate-200'}`}
                      data-testid={`season-card-${season.id}`}
                    >
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <CardTitle className="text-lg flex items-center gap-2">
                              {season.name}
                              {season.is_active && (
                                <Badge className="bg-green-500 text-white">
                                  <Check className="h-3 w-3 mr-1" />
                                  Active
                                </Badge>
                              )}
                            </CardTitle>
                            <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
                              <Calendar className="h-4 w-4" />
                              {season.start_date || season.end_date ? (
                                <span>{formatDate(season.start_date)} - {formatDate(season.end_date)}</span>
                              ) : (
                                <span>No date range set</span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => openEditSeasonDialog(season)}
                              data-testid={`edit-season-${season.id}`}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            {!season.is_active && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                onClick={() => openDeleteSeasonDialog(season.id)}
                                data-testid={`delete-season-${season.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0">
                        {/* Hours Preview */}
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm mb-4">
                          {(season.hours || []).slice(0, 4).map(h => (
                            <div key={h.day} className="flex justify-between">
                              <span className="text-slate-500 capitalize">{h.day.slice(0, 3)}</span>
                              <span className={h.is_open ? 'text-slate-700' : 'text-red-500'}>
                                {h.is_open ? `${formatTime(h.open_time)} - ${formatTime(h.close_time)}` : 'Closed'}
                              </span>
                            </div>
                          ))}
                        </div>
                        
                        {!season.is_active && (
                          <Button
                            variant="outline"
                            className="w-full border-green-300 text-green-600 hover:bg-green-50"
                            onClick={() => handleActivateSeason(season.id)}
                            data-testid={`activate-season-${season.id}`}
                          >
                            <Check className="h-4 w-4 mr-2" />
                            Set as Active Season
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>

              {/* Info Card */}
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="py-4">
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-blue-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-blue-900">How Seasons Work</p>
                      <p className="text-sm text-blue-700 mt-1">
                        Only one season can be active at a time. The active season's hours will be displayed 
                        to parents and customers on the Contact Us page. Use start and end dates to plan 
                        ahead for seasonal changes.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Timezone Tab */}
          <TabsContent value="timezone">
            <div className="space-y-6">
              <Card className="border border-slate-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="h-5 w-5" />
                    Timezone Configuration
                  </CardTitle>
                  <p className="text-sm text-slate-500">
                    Set the timezone for your academy. All dates and times throughout the application will use this timezone.
                  </p>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Current Server Time */}
                  {currentServerTime && (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-blue-800">Current Server Time</p>
                          <p className="text-2xl font-bold text-blue-900">
                            {currentServerTime.local_time_display}
                          </p>
                          <p className="text-sm text-blue-700">
                            {currentServerTime.local_date} ({currentServerTime.timezone_abbreviation})
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={fetchCurrentTime}
                          className="border-blue-300 text-blue-700 hover:bg-blue-100"
                        >
                          <Clock className="h-4 w-4 mr-1" />
                          Refresh
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Timezone Selection */}
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Business Timezone</Label>
                      <select
                        value={timezoneSettings.timezone}
                        onChange={(e) => setTimezoneSettings(prev => ({ ...prev, timezone: e.target.value }))}
                        className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        data-testid="timezone-select"
                      >
                        {timezoneSettings.available_timezones?.map((tz) => (
                          <option key={tz.value} value={tz.value}>
                            {tz.label}
                          </option>
                        ))}
                      </select>
                      <p className="text-sm text-slate-500">
                        This timezone will be used for all date/time displays, scheduling, coach hours logging, and booking systems.
                      </p>
                    </div>
                    
                    <div className="flex justify-end">
                      <Button
                        onClick={handleSaveTimezone}
                        disabled={savingTimezone}
                        className="bg-blue-500 hover:bg-blue-600"
                        data-testid="save-timezone-btn"
                      >
                        {savingTimezone ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="h-4 w-4 mr-2" />
                            Save Timezone
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                  
                  {/* Timezone Info */}
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex gap-2">
                      <Calendar className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-amber-800">Important Note</p>
                        <p className="text-sm text-amber-700 mt-1">
                          Changing the timezone will affect how dates and times are displayed across the entire application, 
                          including coach hours, class schedules, bookings, and attendance records. 
                          Existing data stored in UTC will be converted to display in the new timezone.
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Contact Info Tab */}
          <TabsContent value="contact">
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <h3 className="text-lg font-medium">Contact Information</h3>
                  <p className="text-sm text-slate-500">Manage your academy's contact details</p>
                </div>
                <Button
                  onClick={handleSaveSettings}
                  disabled={saving}
                  className="bg-blue-500 hover:bg-blue-600"
                  data-testid="save-settings-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Settings
                    </>
                  )}
                </Button>
              </div>

              {/* Basic Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-blue-500" />
                    Basic Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Business Name</Label>
                      <Input
                        value={settings.business_name}
                        onChange={(e) => updateSettings('business_name', e.target.value)}
                        data-testid="business-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Website</Label>
                      <div className="relative">
                        <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          value={settings.website}
                          onChange={(e) => updateSettings('website', e.target.value)}
                          placeholder="https://www.example.com"
                          className="pl-10"
                          data-testid="website-input"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Tagline</Label>
                    <Input
                      value={settings.business_tagline || ''}
                      onChange={(e) => updateSettings('business_tagline', e.target.value)}
                      placeholder="A brief tagline displayed below the business name on the homepage"
                      data-testid="business-tagline-input"
                    />
                    <p className="text-xs text-slate-500">This will appear below your business name on the homepage</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Address</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                      <Input
                        value={settings.address}
                        onChange={(e) => updateSettings('address', e.target.value)}
                        className="pl-10"
                        data-testid="address-input"
                      />
                    </div>
                  </div>
                  {/* iter323 — Configurable external link surfaced in
                      the dashboard sidebar (e.g. merch store, donation
                      page, parent resources, social feed). Both fields
                      must be filled — left blank, the link is hidden. */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Sidebar Link — Label</Label>
                      <Input
                        value={settings.external_link_label || ''}
                        onChange={(e) => updateSettings('external_link_label', e.target.value)}
                        placeholder="e.g. Shop Merchandise, Donate, Parent Hub"
                        data-testid="external-link-label-input"
                      />
                      <p className="text-xs text-slate-500">Text shown on the sidebar button.</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Sidebar Link — URL</Label>
                      <Input
                        value={settings.external_link_url || ''}
                        onChange={(e) => updateSettings('external_link_url', e.target.value)}
                        placeholder="https://example.com"
                        data-testid="external-link-url-input"
                      />
                      <p className="text-xs text-slate-500">Opens in a new tab. Leave both blank to hide the link.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Logo Upload */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <ImageIcon className="h-5 w-5 text-indigo-500" />
                    Business Logo
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-slate-500">
                    Upload your academy logo. This will be displayed on the homepage and other public pages.
                  </p>
                  
                  {/* Logo Preview */}
                  {settings.logo_id ? (
                    <div className="flex items-start gap-4">
                      <div className="relative">
                        <div className="w-32 h-32 rounded-lg border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center">
                          <img 
                            src={`${(typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL)}/api/admin/logo/${settings.logo_id}`}
                            alt="Business Logo"
                            className="max-w-full max-h-full object-contain"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          <div className="hidden items-center justify-center text-slate-400">
                            <ImageIcon className="h-8 w-8" />
                          </div>
                        </div>
                        <Button
                          variant="destructive"
                          size="icon"
                          className="absolute -top-2 -right-2 h-6 w-6 rounded-full"
                          onClick={handleLogoDelete}
                          disabled={deletingLogo}
                          data-testid="delete-logo-btn"
                        >
                          {deletingLogo ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <X className="h-3 w-3" />
                          )}
                        </Button>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-700">Current Logo</p>
                        <p className="text-xs text-slate-500 mt-1">
                          Click the X button to remove, or upload a new image to replace it.
                        </p>
                        <label className="mt-3 inline-flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-md cursor-pointer hover:bg-slate-50 transition-colors">
                          <Upload className="h-4 w-4 text-slate-500" />
                          <span className="text-sm text-slate-600">Replace Logo</span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleLogoUpload}
                            className="hidden"
                            disabled={uploadingLogo}
                            data-testid="logo-upload-replace"
                          />
                        </label>
                      </div>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-slate-300 transition-colors">
                      <ImageIcon className="h-10 w-10 mx-auto text-slate-300 mb-3" />
                      <p className="text-sm text-slate-500 mb-3">No logo uploaded yet</p>
                      <label className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-md cursor-pointer hover:bg-blue-600 transition-colors">
                        {uploadingLogo ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="h-4 w-4" />
                            Upload Logo
                          </>
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleLogoUpload}
                          className="hidden"
                          disabled={uploadingLogo}
                          data-testid="logo-upload-input"
                        />
                      </label>
                      <p className="text-xs text-slate-400 mt-2">PNG, JPG, or SVG. Max 5MB.</p>
                    </div>
                  )}
                  
                  {/* Logo Size Selector */}
                  <div className="mt-6 pt-6 border-t border-slate-200">
                    <Label className="text-sm font-medium mb-3 block">Display Size</Label>
                    <p className="text-xs text-slate-500 mb-3">
                      Control how large the logo appears on the homepage and dashboard.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {logoSizeOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => handleLogoSizeChange(option.value)}
                          disabled={savingLogoSize}
                          className={`
                            px-4 py-2 rounded-lg border text-sm font-medium transition-all
                            ${settings.logo_size === option.value
                              ? 'bg-blue-500 text-white border-blue-500'
                              : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300 hover:bg-blue-50'
                            }
                            ${savingLogoSize ? 'opacity-50 cursor-not-allowed' : ''}
                          `}
                          data-testid={`logo-size-${option.value}`}
                        >
                          {option.label}
                          <span className="text-xs ml-1 opacity-70">({option.height})</span>
                        </button>
                      ))}
                    </div>
                    
                    {/* Logo Alignment Selector */}
                    <div className="mt-6">
                      <Label className="text-sm font-medium mb-3 block">Alignment</Label>
                      <p className="text-xs text-slate-500 mb-3">
                        Control how the logo is aligned in the header.
                      </p>
                      <div className="flex gap-2">
                        {logoAlignmentOptions.map((option) => {
                          const IconComponent = option.value === 'left' ? AlignLeft : 
                                               option.value === 'center' ? AlignCenter : AlignRight;
                          return (
                            <button
                              key={option.value}
                              onClick={() => handleLogoAlignmentChange(option.value)}
                              disabled={savingLogoAlignment}
                              className={`
                                flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all
                                ${settings.logo_alignment === option.value
                                  ? 'bg-blue-500 text-white border-blue-500'
                                  : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300 hover:bg-blue-50'
                                }
                                ${savingLogoAlignment ? 'opacity-50 cursor-not-allowed' : ''}
                              `}
                              data-testid={`logo-align-${option.value}`}
                            >
                              <IconComponent className="h-4 w-4" />
                              {option.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    
                    {/* Combined Preview */}
                    <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500 mb-2">Preview:</p>
                      <div className="bg-slate-800 p-3 rounded-md">
                        <div className={`flex ${
                          settings.logo_alignment === 'center' ? 'justify-center' : 
                          settings.logo_alignment === 'right' ? 'justify-end' : 'justify-start'
                        }`}>
                          {settings.logo_id ? (
                            <img 
                              src={`${(typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL)}/api/admin/logo/${settings.logo_id}`}
                              alt="Logo Preview"
                              style={{ 
                                height: logoSizeOptions.find(o => o.value === settings.logo_size)?.height || '56px',
                                width: 'auto'
                              }}
                              className="object-contain"
                            />
                          ) : (
                            <span className="text-slate-500 text-sm">No logo uploaded</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Background Image */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <ImageIcon className="h-5 w-5 text-purple-500" />
                    Website Background Image
                  </CardTitle>
                  <p className="text-sm text-slate-500">
                    Upload a background image that will appear on all pages with adjustable transparency
                  </p>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Background Preview */}
                  <div className="border rounded-lg p-4 bg-slate-50">
                    {settings.background_image_url ? (
                      <div className="space-y-4">
                        <div 
                          className="relative h-48 rounded-lg overflow-hidden border"
                          style={{
                            backgroundImage: `url(${(typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL)}${settings.background_image_url})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center'
                          }}
                        >
                          <div 
                            className="absolute inset-0 bg-slate-900"
                            style={{ opacity: 1 - settings.background_opacity }}
                          />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <p className="text-white font-medium bg-black/50 px-4 py-2 rounded">
                              Preview (Opacity: {Math.round(settings.background_opacity * 100)}%)
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => document.getElementById('background-upload-replace').click()}
                            disabled={uploadingBackground}
                          >
                            {uploadingBackground ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Upload className="h-4 w-4 mr-2" />
                            )}
                            Replace
                          </Button>
                          <input
                            id="background-upload-replace"
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleBackgroundUpload}
                          />
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={handleBackgroundDelete}
                            disabled={deletingBackground}
                          >
                            {deletingBackground ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4 mr-2" />
                            )}
                            Remove
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <ImageIcon className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                        <p className="text-sm text-slate-500 mb-3">No background image uploaded</p>
                        <Button
                          variant="outline"
                          onClick={() => document.getElementById('background-upload-input').click()}
                          disabled={uploadingBackground}
                        >
                          {uploadingBackground ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Upload className="h-4 w-4 mr-2" />
                          )}
                          Upload Background Image
                        </Button>
                        <input
                          id="background-upload-input"
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleBackgroundUpload}
                        />
                        <p className="text-xs text-slate-400 mt-2">Recommended: 1920x1080 or larger, max 10MB</p>
                      </div>
                    )}
                  </div>

                  {/* Opacity Slider */}
                  {settings.background_image_url && (
                    <div className="space-y-3">
                      <Label className="flex items-center justify-between">
                        <span>Background Opacity</span>
                        <span className="text-sm text-slate-500 font-normal">
                          {Math.round(settings.background_opacity * 100)}%
                          {savingOpacity && <Loader2 className="inline h-3 w-3 ml-2 animate-spin" />}
                        </span>
                      </Label>
                      <input
                        type="range"
                        min="0.05"
                        max="0.5"
                        step="0.05"
                        value={settings.background_opacity}
                        onChange={(e) => handleOpacityChange(e.target.value)}
                        className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-purple-500"
                      />
                      <div className="flex justify-between text-xs text-slate-400">
                        <span>Subtle (5%)</span>
                        <span>Bold (50%)</span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Contact Methods */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Phone className="h-5 w-5 text-green-500" />
                    Contact Methods
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Phone Number</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          value={settings.business_phone}
                          onChange={(e) => updateSettings('business_phone', e.target.value)}
                          placeholder="123-456-7890"
                          className="pl-10"
                          data-testid="phone-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>WhatsApp Number</Label>
                      <div className="relative">
                        <MessageCircle className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          value={settings.whatsapp_number}
                          onChange={(e) => updateSettings('whatsapp_number', e.target.value)}
                          placeholder="Include country code"
                          className="pl-10"
                          data-testid="whatsapp-input"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Delivery Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        value={settings.delivery_email}
                        onChange={(e) => updateSettings('delivery_email', e.target.value)}
                        placeholder="contact@academy.com"
                        className="pl-10"
                        data-testid="email-input"
                      />
                    </div>
                    <p className="text-xs text-slate-500">This email will be used as the "from" address for system emails</p>
                  </div>
                </CardContent>
              </Card>

              {/* Social Links */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Globe className="h-5 w-5 text-purple-500" />
                    Social Media Links
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Facebook</Label>
                      <Input
                        value={settings.social_links?.facebook || ''}
                        onChange={(e) => updateSocialLink('facebook', e.target.value)}
                        placeholder="https://facebook.com/yourpage"
                        data-testid="facebook-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Instagram</Label>
                      <Input
                        value={settings.social_links?.instagram || ''}
                        onChange={(e) => updateSocialLink('instagram', e.target.value)}
                        placeholder="https://instagram.com/yourpage"
                        data-testid="instagram-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2 max-w-md">
                    <Label>Twitter / X</Label>
                    <Input
                      value={settings.social_links?.twitter || ''}
                      onChange={(e) => updateSocialLink('twitter', e.target.value)}
                      placeholder="https://twitter.com/yourpage"
                      data-testid="twitter-input"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Branding Tab (iter313) — UI theme + colour palette */}
          <TabsContent value="branding">
            <div className="space-y-6" data-testid="branding-tab-content">
              <ThemePicker />
            </div>
          </TabsContent>

          {/* Maintenance Tab */}
          <TabsContent value="maintenance">
            <div className="space-y-6">
              <Card className={settings.maintenance_mode ? 'border-red-300 bg-red-50' : ''}>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <ShieldOff className={`h-5 w-5 ${settings.maintenance_mode ? 'text-red-500' : 'text-slate-500'}`} />
                    Maintenance Mode
                    {settings.maintenance_mode && (
                      <Badge className="bg-red-500 text-white ml-2">ACTIVE</Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {settings.maintenance_mode && (
                    <div className="p-3 bg-red-100 border border-red-300 rounded-lg flex items-start gap-2">
                      <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-red-800">Maintenance Mode is Active</p>
                        <p className="text-sm text-red-700">Login is disabled for all users except admins. The landing page remains accessible.</p>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium">Enable Maintenance Mode</p>
                      <p className="text-sm text-slate-500">Disable login during updates or maintenance (landing page stays accessible)</p>
                    </div>
                    <Switch
                      checked={settings.maintenance_mode}
                      onCheckedChange={(checked) => updateSettings('maintenance_mode', checked)}
                      data-testid="maintenance-mode-toggle"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Maintenance Message</Label>
                    <Textarea
                      value={settings.maintenance_message || ''}
                      onChange={(e) => updateSettings('maintenance_message', e.target.value)}
                      placeholder="Message to display to users when they try to login..."
                      rows={3}
                      data-testid="maintenance-message-input"
                    />
                    <p className="text-xs text-slate-500">This message will be shown when users try to login during maintenance.</p>
                  </div>

                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-medium text-blue-800 mb-2">What happens when maintenance mode is enabled?</h4>
                    <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                      <li>Landing page remains fully accessible</li>
                      <li>Login is disabled for all users except admins</li>
                      <li>Users see the maintenance message when trying to login</li>
                      <li>Admins can still login to manage the system</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* iter307ch — Automations Tab */}
          <TabsContent value="automations">
            <AutomationsTab />
          </TabsContent>

          {/* iter334cg — Plan & Billing (read-only for tenant admin) */}
          <TabsContent value="plan-billing">
            <PlanAndBillingTab />
          </TabsContent>
        </Tabs>
      </div>

      {/* Delete Season Confirmation Dialog */}
      <AlertDialog open={deleteSeasonDialog} onOpenChange={setDeleteSeasonDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-600" />
              Delete Season
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this season? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSeason}
              className="bg-red-500 hover:bg-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Season Create/Edit Dialog */}
      <Dialog open={showSeasonDialog} onOpenChange={setShowSeasonDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="season-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sun className="h-5 w-5 text-amber-500" />
              {editingSeason ? 'Edit Season' : 'Create New Season'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Season Info */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Season Name *</Label>
                <Input
                  value={seasonForm.name}
                  onChange={(e) => setSeasonForm({ ...seasonForm, name: e.target.value })}
                  placeholder="e.g., Summer Season, Winter Hours, Holiday Schedule"
                  data-testid="season-name-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={seasonForm.start_date}
                    onChange={(e) => setSeasonForm({ ...seasonForm, start_date: e.target.value })}
                    data-testid="season-start-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={seasonForm.end_date}
                    onChange={(e) => setSeasonForm({ ...seasonForm, end_date: e.target.value })}
                    data-testid="season-end-date"
                  />
                </div>
              </div>
              <p className="text-xs text-slate-500">
                Dates are for reference only. You must manually activate the season when ready.
              </p>
            </div>

            {/* Hours Configuration */}
            <div className="space-y-3">
              <Label className="text-base font-medium">Operating Hours</Label>
              <div className="border rounded-lg divide-y">
                {DAYS.map(({ key, label }) => {
                  const dayHours = seasonForm.hours.find(h => h.day === key) || { day: key, is_open: true, open_time: '09:00', close_time: '17:00' };
                  return (
                    <div 
                      key={key} 
                      className={`p-3 flex items-center gap-4 ${!dayHours.is_open ? 'bg-slate-50' : ''}`}
                    >
                      <div className="w-28">
                        <p className="font-medium text-slate-900">{label}</p>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={dayHours.is_open}
                          onCheckedChange={(checked) => updateSeasonHours(key, 'is_open', checked)}
                        />
                        <span className={`text-sm w-12 ${dayHours.is_open ? 'text-green-600' : 'text-slate-400'}`}>
                          {dayHours.is_open ? 'Open' : 'Closed'}
                        </span>
                      </div>

                      {dayHours.is_open && (
                        <div className="flex items-center gap-2 flex-1">
                          <Input
                            type="time"
                            value={dayHours.open_time || '09:00'}
                            onChange={(e) => updateSeasonHours(key, 'open_time', e.target.value)}
                            className="w-32"
                          />
                          <span className="text-slate-400">to</span>
                          <Input
                            type="time"
                            value={dayHours.close_time || '17:00'}
                            onChange={(e) => updateSeasonHours(key, 'close_time', e.target.value)}
                            className="w-32"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSeasonDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveSeason} 
              disabled={saving}
              className="bg-blue-500 hover:bg-blue-600"
              data-testid="save-season-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {editingSeason ? 'Update Season' : 'Create Season'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default BusinessHours;
